import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import {
    DxChartModule,
    DxPieChartModule,
    DxTabsModule,
    DxButtonComponent,
    DxDateRangeBoxComponent,
} from 'devextreme-angular';
import {
    AggregateType,
    EmptyTrailerScoreQuery,
    EmptyTrailerScoreType,
    IMyScoreShiftQuery,
    ScoreDto,
    YardCapacityScoreExportQuery,
    YardManagementClient,
} from '@services';
import { BehaviorSubject, EMPTY, Observable, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, switchMap } from 'rxjs/operators';
import {
    ChartDataPoint,
    DoughnutChartComponent,
} from 'src/app/components/library/doughnut-chart/doughnut-chart.component';
import notify from 'devextreme/ui/notify';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FileUtils } from 'src/app/shared/file-utils';
import EnumUtils from 'src/app/shared/enum-utils';
import { ScoreBreakdownChartComponent } from '../components/score-breakdown-chart/score-breakdown-chart.component';

export function emptyTrailerPalette(): Record<string, string> {
    const getColor = (name: string) =>
        getComputedStyle(document.body).getPropertyValue(name).trim();

    return {
        ['Empty Required']: getColor('--bs-success'),
        ['Empty Not Required (<24)']: getColor('--bs-warning'),
        ['Empty Not Required (>24)']: getColor('--bs-danger'),
        ['Ready For Checkout']: getColor('--bs-primary'),
        ['Checked Out']: getColor('--bs-secondary'),
    };
}

@Component({
    selector: 'app-empty-trailer-score',
    imports: [
    CommonModule,
    DxPieChartModule,
    DxButtonComponent,
    ScoreBreakdownChartComponent,
    DoughnutChartComponent,
    DxChartModule,
    DxDateRangeBoxComponent,
    DxTabsModule,
],
    providers: [CurrencyPipe],
    templateUrl: './empty-trailer-score.component.html',
})
export class EmptyTrailerScoreComponent implements OnInit, OnDestroy {
    private yardManagementClient = inject(YardManagementClient);
    private destroyRef = inject(DestroyRef);

    trendingFilterOptions = EnumUtils.getEnumOptions(AggregateType);
    trendingFilterValue: AggregateType = AggregateType.Day;

    dateRangeFilterValue: [Date, Date] = [
        new Date(new Date().setHours(0, 0, 0, 0)),
        new Date(new Date().setHours(0, 0, 0, 0)),
    ];

    private readonly scoreQuery$ = new Subject<IMyScoreShiftQuery>();

    emptyTrailers: Observable<{
        [key in keyof typeof EmptyTrailerScoreType]?: ScoreDto;
    }> = this.scoreQuery$.pipe(
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        switchMap((query) =>
            this.yardManagementClient
                .getEmptyTrailerScore(
                    new EmptyTrailerScoreQuery({ from: query.from, to: query.to })
                )
                .pipe(
                    catchError(() => {
                        notify('Failed to load empty trailer', 'error', 3000);
                        return EMPTY;
                    })
                )
        ),
        shareReplay(1)
    );

    mainPalette: Record<string, string> = {};
    scoreData: ChartDataPoint[] = [];
    daysLookBack: number = 0;
    requiredItem: ScoreDto | null = null;
    notRequiredItem: ScoreDto | null = null;

    private readonly dateRange$ = new BehaviorSubject<[Date, Date]>([
        new Date(),
        new Date(),
    ]);

    loadMainScoreData = (query: IMyScoreShiftQuery) => {
        this.scoreQuery$.next(query);
        return this.emptyTrailers.pipe(map((scores) => scores[EmptyTrailerScoreType.MainScore]));
    };

    ngOnInit(): void {
        this.mainPalette = emptyTrailerPalette();

        this.emptyTrailers.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((scores) => {
            this.requiredItem = scores[EmptyTrailerScoreType.Required] ?? null;
            this.notRequiredItem = scores[EmptyTrailerScoreType.NotRequired] ?? null;
        });
    }

    ngOnDestroy(): void {
        this.dateRange$.complete();
    }

    onDateRangeChanged(e: any): void {
        if (e.value) {
            this.dateRangeFilterValue = e.value;
        }
    }

    onMainChartLoaded(items: ChartDataPoint[]) {
        this.scoreData = items;
    }

    get fromDate(): Date {
        return this.dateRangeFilterValue?.[0];
    }

    get toDate(): Date {
        return this.dateRangeFilterValue?.[1];
    }

    onExport(): void {
        const query = new YardCapacityScoreExportQuery({
            from: this.fromDate,
            to: this.toDate,
        });

        this.yardManagementClient
            .exportEmptyTrailer(query)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((res) => FileUtils.downloadFile(res));
    }
}
